import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import styles from './CallHistoryRow.module.scss';
import Icon, { IconName } from '../../../../components/Icon';
import { NumberBlockCheck, PhonebookContactEntity, UserEntity } from '../../../../types';
import {
    addOneToast,
    findContactOnBook,
    isValidContactEntity,
    selectCanBlockNumbers,
    selectIsNumberBlocking,
    setActiveChat,
    setEditPhonebookContact,
    setViewContact,
    startCall
} from '../../../../redux/slices';
import AriaButton from '../../../../components/AriaComponents/AriaButton';
import { defaultContact } from './ExternalCallHistoryRow';
import LoadingSpinner from '../../../../components/loading/LoadingSpinner';
import { usePolyglot } from '../../../../context/Polyglot';
import { useTypedDispatch, useTypedSelector } from '../../../../redux/hooks';
import CallIconButton from '../../../../components/buttons/CallIconButton';
import { useUserStatusContext } from '../../../home/children/UserStatusContext';
import StyledDropdown from '../../../../components/StyledComponents/StyledDropdown';
import { contactsApi } from '../../../../redux/services/contactsApi';
import { useNumberBlockContext } from '../../GetNumberBlockingContext';

interface Props {
    answered: boolean;
    callIcon: IconName;
    rowName: string;
    secondaryString: string | undefined;
    subHeader: ReactNode;
    date: string;
    duration: string;
    options: ReactNode;
    elsewhere?: boolean;
    blocked: NumberBlockCheck | undefined
}

interface ContactOptionsProps {
    contactEntity: PhonebookContactEntity | undefined;
    callee: string;
    displayNumber?: string;
}

interface UserOptionsProps {
    userEntity: UserEntity | undefined;
    callee: string;
}

export const ContactCdrOptions: React.FC<ContactOptionsProps> = ({
    contactEntity,
    callee,
    displayNumber
}) => {
    const { addNumberForCheck } = useNumberBlockContext();
    const canBlock = useTypedSelector(selectCanBlockNumbers)

    const numberBlocking = useTypedSelector(state => selectIsNumberBlocking(state, callee));

    const blocking = numberBlocking?.blocking;

    const [contactLoading, setContactLoading] = useState(false);

    const { t } = usePolyglot();
    const dispatch = useTypedDispatch();

    const openContact = () => {
        if (!contactEntity) {
            setContactLoading(false);
            return;
        }
        const phonebookUuid =
            contactEntity.phonebookUuid || contactEntity.details[0]?.phonebook_uuid;
        const contactUuid = contactEntity.uuid || contactEntity.details[0]?.uuid;

        if (!phonebookUuid || !contactUuid) return;

        dispatch(
            findContactOnBook({
                phonebookUuid,
                contactUuid
            })
        )
            .then(r => {
                if (!r) {
                    dispatch(
                        addOneToast({
                            content: `Failed to find ${contactEntity.name || 'contact'
                                } in contacts`,
                            title: 'Failed to find contact',
                            type: 'error'
                        })
                    );
                }
                dispatch(setViewContact(r));
            })
            .finally(() => {
                setContactLoading(false);
            });
    };

    useEffect(() => {
        if (!numberBlocking) {
            addNumberForCheck(callee)
        }
    }, [numberBlocking])

    return (
        <>
            <AriaButton
                onClick={() => {
                    if (isValidContactEntity(contactEntity)) {
                        setContactLoading(true);
                        openContact();
                        return;
                    }
                    dispatch(
                        setEditPhonebookContact(defaultContact(t('phrases.label_main'), callee))
                    );
                }}
                title={
                    isValidContactEntity(contactEntity)
                        ? t('actions.view_contact')
                        : t('actions.add_contact', 1)
                }
                disabled={contactLoading}
            >
                {contactLoading ? <LoadingSpinner /> : null}
                {!contactLoading && isValidContactEntity(contactEntity) ? (
                    <Icon name='accountFilled' width={22} height={22} />
                ) : null}
                {!contactLoading && !isValidContactEntity(contactEntity) ? (
                    <Icon name='addContact' width={22} height={22} />
                ) : null}
            </AriaButton>
            <AriaButton
                onClick={() => {
                    dispatch(startCall(displayNumber || callee, contactEntity?.name));
                }}
                disabled={blocking?.outbound}
                title={blocking?.outbound ? 'Outbound calls to this number are blocked' : undefined}
            >
                <Icon name='phoneCall' width={22} height={22} />
            </AriaButton>
            {canBlock ? (
                <BlockingNumber
                    inbound={blocking?.inbound ?? false}
                    outbound={blocking?.outbound ?? false}
                    number={numberBlocking?.number || callee}
                    isHistory
                />
            ) : null}
        </>
    );
};

export const BlockingNumber: React.FC<{ inbound: boolean, outbound: boolean, number: string, isHistory?: boolean }> = ({ inbound, outbound, number, isHistory }) => {
    const { updateBlockingState } = useNumberBlockContext();
    const [removeBlockList, { isLoading: removeLoading }] = contactsApi.endpoints.removeNumberBlock.useMutation();

    const options = useMemo(() => {
        const inboundText = inbound ? 'Unblock Inbound Calls' : 'Block Inbound Calls';
        const outboundText = outbound ? 'Unblock Outbound Calls' : 'Block Outbound Calls';

        const inboundCmp = <p>{inboundText}{isHistory ? (<Icon name='cancel' />) : null}</p>
        const outboundCmp = <p>{outboundText}{isHistory ? (<Icon name='cancel' />) : null}</p>

        return [
            {
                label: inboundCmp,
                value: 'inbound',
                title: '',
            },
            {
                label: outboundCmp,
                value: 'outbound',
                title: '',
            }
        ]
    }, [inbound, outbound])

    return (
        <StyledDropdown
            iconOnly
            iconName={isHistory ? undefined : 'cancel'}
            loading={removeLoading}
            className={isHistory ? [styles.is_hist, styles.block_drop_down].join(" ") : styles.block_drop_down}
            options={options}
            onSelect={(direction: string) => {
                if (direction === 'inbound') {
                    if (inbound) {
                        removeBlockList({
                            number,
                            direction
                        });
                    } else {
                        updateBlockingState({ number, is_outbound: false });
                    }
                } else if (direction === "outbound") {
                    if (outbound) {
                        removeBlockList({
                            number,
                            direction
                        });
                    } else {
                        updateBlockingState({ number, is_outbound: true });
                    }
                }
            }}
        />
    )
}

export const UserCdrOptions: React.FC<UserOptionsProps> = ({ userEntity, callee }) => {
    const dispatch = useTypedDispatch();
    const { t } = usePolyglot();

    const { addNameForReq } = useUserStatusContext();

    useEffect(() => {
        const returnFunc = () => {
            if (userEntity) {
                addNameForReq(userEntity.sip_name, false)
            }
        }

        if (!userEntity) return returnFunc;

        addNameForReq(userEntity.sip_name, true)
        return returnFunc;
    }, [userEntity]);

    if (!userEntity) {
        return (
            <AriaButton
                onClick={() => {
                    dispatch(startCall(callee));
                }}
            >
                <Icon name='phoneCall' width={22} height={22} />
            </AriaButton>
        );
    }

    return (
        <>
            <CallIconButton
                handleCall={() => {
                    dispatch(startCall(callee, userEntity.name));
                }}
                user={{ uuid: userEntity.uuid, name: userEntity.sip_name }}
            />
            <AriaButton
                onClick={() => {
                    dispatch(setActiveChat(userEntity.uuid));
                }}
                title={t('actions.send_message')}
            >
                <Icon name='messageFilled' width={22} height={22} />
            </AriaButton>
        </>
    );
};

export function numberBlockedText(blocking?: NumberBlockCheck): string {
    if (!blocking || (!blocking.inbound && !blocking.outbound)) return "";

    if (blocking.inbound && blocking.outbound) {
        return " (Calls blocked)";
    }

    if (blocking.outbound) {
        return " (Outbound blocked)";
    }

    return " (Inbound blocked)";
}

const CallHistoryRowDisplay: React.FC<Props> = ({
    answered,
    callIcon,
    rowName,
    secondaryString,
    subHeader,
    date,
    duration,
    options,
    elsewhere,
    blocked
}) => (
    <li
        className={styles.container}
        data-answered={answered}
        data-has-options={!!options || null}
        // onClick={() => (options && displayNumber) && dispatch(startCall(displayNumber))}
        // title={(options && displayNumber) ? t("actions.start_call") : ''}
        /* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */
        tabIndex={0}
        data-elsewhere={elsewhere || null}
    >
        <div className={styles.callStatus}>
            <Icon name={callIcon} />
        </div>
        <div className={`${styles.caller} ${styles.dataRows}`}>
            <div className={styles.callerName}>
                <p>{rowName}</p>
                {secondaryString || (blocked?.inbound || blocked?.outbound) ? <p>{secondaryString}{numberBlockedText(blocked)}</p> : null}
            </div>
            <p>{subHeader}</p>
        </div>
        <div className={`${styles.timeData} ${styles.dataRows}`}>
            <p>{date}</p>
            <p>{duration}</p>
        </div>
        {options ? <div className={styles.hoverMenu}>{options}</div> : null}
    </li>
);

export default CallHistoryRowDisplay;
